/**
 * AI Edit Service
 * 
 * Provides client-side utilities for streaming AI-powered chart edits
 * using Server-Sent Events (SSE).
 * 
 * Uses the optimal event structure with:
 * - event_id for deduplication
 * - correlation_id for tracing
 * - sequence for ordering
 * - type and category for classification
 * - Rich metadata for observability
 */

import { getApiUrl, STORAGE_KEYS } from './config';
import api, { handleUnauthorizedResponse } from './client';
import { readSseStream, supportErrorMessage } from './sse';

// ─── Types mirroring api/src/types/redis.generated.ts ────────────────────────

export type EventCategory = 'status' | 'stream' | 'result' | 'error' | 'system';

export type EventType =
  | 'job.started'
  | 'job.progress'
  | 'job.completed'
  | 'job.failed'
  | 'job.cancelled'
  | 'system.heartbeat';

export interface EventMetadata {
  user_id?: string | null;
  chart_id?: string | null;
  connection_id?: string | null;
  version?: string;
  source?: string | null;
}

export interface JobEvent {
  event_id?: string;
  job_id: string;
  correlation_id: string;
  timestamp?: number;
  sequence: number;
  type: EventType;
  category: EventCategory;
  data: Record<string, unknown>;
  metadata?: EventMetadata | null;
}

// ─── Progress sub-types (discriminated by sub_type) ──────────────────────────

export interface StatusProgress {
  stage: string;
  progress: number;
  message: string;
  sub_type?: 'status';
  details?: Record<string, unknown> | null;
}

export interface TextDeltaProgress {
  stage: string;
  progress: number;
  message: string;
  sub_type?: 'text_delta';
  delta: string;
  accumulated: string;
  is_final?: boolean;
}

export interface ChartDiffProgress {
  stage: string;
  progress: number;
  message: string;
  sub_type?: 'chart_diff';
  operation: 'add' | 'modify' | 'remove';
  path: string[];
  old_value?: unknown;
  new_value?: unknown;
  patch: Record<string, unknown>;
  explanation?: string | null;
}

export interface JobCompletedData {
  stage?: string;
  progress?: number;
  message?: string;
  duration_ms: number;
  summary: Record<string, unknown>;
  result: Record<string, unknown>;
}

export interface JobFailedData {
  stage?: string;
  progress?: number;
  message: string;
  error?: string | null;
  error_type?: string | null;
  details?: Record<string, unknown> | null;
}

// Suggestion payload — prompt-only (executed by chart_edit skill on click)
export interface AiEditSuggestion {
  title: string;
  description: string;
  reason?: string;
  chart_type?: string;
  /** Rich instruction for the AI chart editor */
  prompt: string;
  /** Controls downstream routing: "new_chart" forces SQL generation, "chart_edit" runs normal classifier */
  suggestion_type?: 'new_chart' | 'chart_edit';
}

// Request structure
export interface AiEditRequest {
  prompt: string;
  history?: Array<{
    role: 'user' | 'assistant';
    content: string;
  }>;
  /** Pre-built suggestion — skips LLM routing, applies config directly */
  suggestion?: AiEditSuggestion;
  /** Redis preview key — provides chart context for new charts */
  preview_chart_id?: string;
  /** Active dashboard filters — passed to explain skill for filtered data */
  filters?: Record<string, unknown>;
}

// Event handlers
export interface AiEditHandlers {
  onJobStarted?: (data: any) => void;
  onProgress?: (stage: string, progress: number, message: string) => void;
  onAiTextDelta?: (delta: string, accumulated: string) => void;
  onAiTextComplete?: (fullText: string) => void;
  onChartDiff?: (diff: any) => void;
  onJobCompleted?: (result: any) => void;
  onError?: (message: string, details?: any) => void;
  onJobCancelled?: () => void;
}

/**
 * Stream AI edit suggestions for a chart
 * 
 * @param chartId - The ID of the chart to edit
 * @param request - The AI edit request data
 * @param handlers - Event handlers for streaming events
 * @returns A function to abort the stream
 */
export async function streamAiEdit(
  chartId: string,
  request: AiEditRequest,
  handlers: AiEditHandlers
): Promise<() => void> {
  const controller = new AbortController();
  const { signal } = controller;

  try {
    // Build the API URL
    const url = getApiUrl(`/charts/${chartId}/ai-edit`);

    // Get auth token if available
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Correlation-ID': crypto.randomUUID(),
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Start the fetch request
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      handleUnauthorizedResponse(response.status);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const processStream = async () => {
      try {
        await readSseStream(response.body!, ({ data }) => {
          try {
            const event = JSON.parse(data) as JobEvent | { type: 'error'; payload?: Record<string, unknown> };
            if (event.type === 'error') {
              const failure = event.payload ?? {};
              handlers.onError?.(supportErrorMessage(failure), failure);
            } else {
              handleEvent(event, handlers);
            }
          } catch (parseError) {
            console.error('Failed to parse SSE event:', parseError);
          }
        });
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Stream was aborted, this is expected
          return;
        }
        throw error;
      }
    };

    // Start processing the stream
    processStream().catch((error) => {
      if (handlers.onError) {
        handlers.onError(error.message || 'Stream processing error');
      }
    });

  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      // Request was aborted, this is expected
      return () => {};
    }
    
    if (handlers.onError) {
      handlers.onError(error instanceof Error ? error.message : 'Failed to start stream');
    }
  }

  // Return abort function
  return () => {
    controller.abort();
  };
}

/**
 * Handle individual SSE events with optimal structure
 */
function handleEvent(event: JobEvent, handlers: AiEditHandlers): void {
  console.log(`[SSE Event] ${event.type} (seq: ${event.sequence})`, event.data);

  switch (event.type) {
    case 'job.started':
      if (handlers.onJobStarted) {
        handlers.onJobStarted(event.data);
      }
      break;

    case 'job.progress': {
      const { sub_type, stage, progress, message } = event.data;

      // Route by sub_type for domain-specific progress events
      if (sub_type === 'text_delta') {
        const { delta, accumulated, is_final } = event.data;
        if (handlers.onAiTextDelta) {
          handlers.onAiTextDelta(delta as string, accumulated as string);
        }
        if (is_final && handlers.onAiTextComplete) {
          handlers.onAiTextComplete(accumulated as string);
        }
      } else if (sub_type === 'chart_diff') {
        if (handlers.onChartDiff) {
          handlers.onChartDiff(event.data);
        }
      } else {
        // sub_type === 'status' or no sub_type (generic progress)
        if (handlers.onProgress) {
          handlers.onProgress(stage as string, progress as number, message as string);
        }
      }
      break;
    }

    case 'job.completed':
      if (handlers.onJobCompleted) {
        handlers.onJobCompleted(event.data.result);
      }
      break;

    case 'job.failed':
      if (handlers.onError) {
        const msg = (event.data.message || event.data.error || 'AI edit failed') as string;
        handlers.onError(supportErrorMessage({
          message: msg,
          details: event.data.details,
        }, 'AI edit failed'), event.data.details);
      }
      break;

    case 'job.cancelled':
      if (handlers.onJobCancelled) {
        handlers.onJobCancelled();
      }
      break;

    default:
      console.warn('Unknown event type:', event.type);
  }
}

// ─── Chat History API ────────────────────────────────────────────────────────

export interface ChatHistoryMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface ChatHistoryResponse {
  messages: ChatHistoryMessage[];
}

/**
 * Fetch persisted chat history for a chart.
 */
export async function getChatHistory(chartId: string, limit?: number): Promise<ChatHistoryMessage[]> {
  const params = limit ? `?limit=${limit}` : '';
  const response = await api.get<ChatHistoryResponse>(
    `/charts/${chartId}/chat-history${params}`
  );
  return response.messages;
}

/**
 * Delete chat history for a chart.
 */
export async function deleteChatHistory(chartId: string): Promise<boolean> {
  const response = await api.delete<{ deleted: boolean }>(
    `/charts/${chartId}/chat-history`
  );
  return response.deleted;
}

export const aiEditService = {
  streamAiEdit,
  getChatHistory,
  deleteChatHistory,
};
